import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Compass, Plus, Bookmark, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const Ctx = createContext(null);
export const useVibe = () => useContext(Ctx);
export const vErr = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Something went wrong");

export function VAvatar({ p, size = 40, ring }) {
  return p?.avatar_url ? <img src={p.avatar_url} alt="" className={`rounded-full object-cover shrink-0 ${ring ? "ring-2 ring-white" : ""}`} style={{ width: size, height: size }} />
    : <span className={`rounded-full shrink-0 flex items-center justify-center font-black text-black bg-gradient-to-br from-[#25F4EE] to-[#FE2C55] ${ring ? "ring-2 ring-white" : ""}`} style={{ width: size, height: size, fontSize: size * 0.42 }}>{(p?.display_name || "?")[0].toUpperCase()}</span>;
}

export function useNeedLogin() {
  const nav = useNavigate();
  const { me } = useVibe();
  return useCallback(() => { if (me) return false; toast("Sign in to join the vibe", { action: { label: "Sign in", onClick: () => nav(`/login?next=${encodeURIComponent(window.location.pathname)}`) } }); return true; }, [me, nav]);
}

export default function VibeLayout() {
  const { user, loading } = useAuth();
  const [me, setMe] = useState(null);
  const reload = useCallback(() => (user ? api.get("/vibe/me").then((r) => setMe(r.data)).catch(() => setMe(null)) : setMe(null)), [user]);
  useEffect(() => { document.title = "JixxeoVibe"; }, []);
  useEffect(() => { if (!loading) reload(); }, [loading, reload]);
  const nav = useNavigate();
  return (
    <Ctx.Provider value={{ me: me?.profile ? me : null, reload }}>
      <div className="fixed inset-0 bg-black text-white flex flex-col" style={{ fontFamily: "'Outfit',ui-sans-serif" }} data-testid="vibe-layout">
        <div className="flex-1 min-h-0 relative"><Outlet /></div>
        <nav className="shrink-0 h-14 bg-black border-t border-white/10 grid grid-cols-5 items-center z-30" data-testid="vibe-nav">
          <Tab to="/vibe" end icon={<Home className="w-6 h-6" />} label="Home" id="home" />
          <Tab to="/vibe/discover" icon={<Compass className="w-6 h-6" />} label="Discover" id="discover" />
          <button onClick={() => nav("/vibe/create")} data-testid="vibe-nav-create" aria-label="Create" className="mx-auto relative w-12 h-8 active:scale-90 transition-transform">
            <span className="absolute inset-0 rounded-lg bg-[#25F4EE] -translate-x-1" /><span className="absolute inset-0 rounded-lg bg-[#FE2C55] translate-x-1" />
            <span className="absolute inset-0 rounded-lg bg-white text-black flex items-center justify-center"><Plus className="w-5 h-5" strokeWidth={3} /></span>
          </button>
          <Tab to="/vibe/saved" icon={<Bookmark className="w-6 h-6" />} label="Saved" id="saved" />
          <Tab to={me?.profile ? `/vibe/u/${me.profile.username}` : "/vibe/me"} icon={<User className="w-6 h-6" />} label="Me" id="me" />
        </nav>
      </div>
    </Ctx.Provider>
  );
}

function Tab({ to, icon, label, id, end }) {
  return <NavLink to={to} end={end} data-testid={`vibe-nav-${id}`} className={({ isActive }) => `flex flex-col items-center text-[10px] font-bold gap-0.5 transition-colors ${isActive ? "text-white" : "text-white/50"}`}>{icon}{label}</NavLink>;
}
